import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  DialogContent,
  Grid,
  IconButton,
  Modal,
  TextField,
  MenuItem,
  InputAdornment,
} from "@mui/material";
import { get, postJsonData } from "../../network/ApiController";
import ApiEndpoints from "../../network/ApiEndPoints";
import { apiErrorToast, okSuccessToast } from "../../utils/ToastUtil";
import ModalHeader from "../../modals/ModalHeader";
import ModalFooter from "../../modals/ModalFooter";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { whiteColor } from "../../theme/setThemeColor";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "40%",
  bgcolor: "background.paper",
  boxShadow: 24,
  fontFamily: "Poppins",
  p: 2,
  height: "max-content",
  overflowY: "scroll",
};

const AddSalary = ({ onSuccess }) => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    user_id: "",
    ctc: "",
    department: "",
  });
  const [loading, setLoading] = useState(false);
  const [developers, setDevelopers] = useState([]);
  const [errors, setErrors] = useState({});

  const departments = ["IT", "Backend", "ASM", "Office Assistant"];// ✅ Department list

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const fetchDevelopers = async () => {
    setLoading(true);
    try {
      await get(
        `${ApiEndpoints.GET_USERS_BIGG}?is_employee=1`, // Add role filter if needed
        {export:1},
        setLoading,
        (res) => {
          setDevelopers(res.data || []);
        },
        (error) => {
          apiErrorToast(error);
        }
      );
    } catch (error) {
      apiErrorToast("Failed to fetch developers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchDevelopers();
      // Reset form when opening
      setFormData({
        user_id: "",
        ctc: "",
        department: "",
      });
      setErrors({});
    }
  }, [open]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.user_id) {
      newErrors.user_id = "Please select a developer";
    }
    if (!formData.ctc) {
      newErrors.ctc = "Please enter CTC amount";
    } else if (isNaN(formData.ctc) || parseFloat(formData.ctc) <= 0) {
      newErrors.ctc = "CTC must be a positive number";
    }
    if (!formData.department) {
      newErrors.department = "Please select a department";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    const payload = {
      user_id: formData.user_id,
      ctc: parseFloat(formData.ctc), // Ensure it's a number
    };

    setLoading(true);

    postJsonData(
      ApiEndpoints.ASSIGN_SALARY, // Using ASSIGN_SALARY instead of ADD_SALARY
      payload,
      setLoading,
      (response) => {
        if (response?.data?.status === 'success') {
          okSuccessToast(response.data.message || "Salary assigned successfully");
          setOpen(false);
          if (onSuccess) onSuccess();
        } else {
          apiErrorToast(response?.data?.message || "Salary creation failed.");
        }
      },
      (error) => {
        apiErrorToast(error.message || "Error creating salary.");
        setLoading(false);
      }
    );
  };

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        startIcon={
          <IconButton sx={{ p: 0, color: whiteColor() }}>
            <AddCircleOutlineIcon />
          </IconButton>
        }
        size="small"
        color="success"
        variant="contained"
        sx={{
          padding: 1,
          backgroundColor: "#2F3B57",
          borderRadius: "2px",
          color: whiteColor(),
        }}
      >
        Add Salary
      </Button>
      <Box>
        <Modal
          open={open}
          onClose={() => setOpen(false)}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box sx={style} className="sm_modal">
            <ModalHeader
              title="Add Salary"
              handleClose={() => setOpen(false)}
            />
            <DialogContent>
              <Grid container spacing={2} mt={1}>
                {/* 🔹 User Dropdown */}
                <Grid item xs={12}>
                  <TextField
                    select
                    fullWidth
                    label="Select Developer *"
                    name="user_id"
                    value={formData.user_id}
                    onChange={handleChange}
                    error={!!errors.user_id}
                    helperText={errors.user_id}
                    size="small"
                    required
                  >
                    {developers.map((dev) => (
                      <MenuItem key={dev.id} value={dev.id}>
                        {dev.name} {dev.mobile ? `(${dev.mobile})` : ''}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                {/* 🔹 Department Dropdown */}
                <Grid item xs={12}>
                  <TextField
                    select
                    fullWidth
                    label="Select Department *"
                    name="department"
                    value={formData.department}
                    onChange={handleChange}
                    error={!!errors.department}
                    helperText={errors.department}
                    size="small"
                    required
                  >
                    {departments.map((dept, index) => (
                      <MenuItem key={index} value={dept}>
                        {dept}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                {/* 🔹 CTC Input (Annual) */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="CTC (Annual) *"
                    name="ctc"
                    type="number"
                    value={formData.ctc}
                    onChange={handleChange}
                    error={!!errors.ctc}
                    helperText={errors.ctc || "Enter annual CTC in rupees"}
                    size="small"
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">₹</InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                {/* Info Text */}
                <Grid item xs={12}>
                  <Box sx={{ 
                    bgcolor: '#e3f2fd', 
                    p: 1.5, 
                    borderRadius: 1,
                    fontSize: '0.875rem',
                    color: '#0d47a1'
                  }}>
                    <strong>Note:</strong> Salary components will be calculated based on this CTC amount.
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>
            <ModalFooter
              btn="Create"
              request={loading}
              onClick={handleSubmit}
            />
          </Box>
        </Modal>
      </Box>
    </>
  );
};

export default AddSalary;